import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Building2, Briefcase, Users, Award, TrendingUp, Clock, DollarSign, Sparkles, Globe2 } from "lucide-react";
import ComplianceAttentionWidget from "../components/ComplianceAttentionWidget";

const StatCard = ({ icon: Icon, label, value, sub, tone = "slate", testId }) => (
  <div data-testid={testId} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between mb-3">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</span>
      <div className={`w-8 h-8 rounded-md bg-${tone}-50 text-${tone}-600 flex items-center justify-center border border-${tone}-100`}>
        <Icon size={15} />
      </div>
    </div>
    <div className="font-display text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
  </div>
);

const DistBar = ({ label, count, max }) => (
  <div className="mb-2">
    <div className="flex justify-between text-xs mb-1">
      <span className="text-slate-700 font-medium">{label}</span>
      <span className="text-slate-500 font-mono">{count}</span>
    </div>
    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
      <div className="h-full bg-sky-500 rounded-full" style={{width: `${(count/max)*100}%`}}></div>
    </div>
  </div>
);

export default function Dashboard() {
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/dashboard/summary").then(r => setD(r.data)); }, []);
  if (!d) return <Layout><div className="text-sm text-slate-500">Loading…</div></Layout>;

  const c = d.candidates, j = d.job_orders, co = d.companies, r = d.revenue;
  const maxCountry = Math.max(1, ...(d.distribution.by_country||[]).map(x=>x.count));
  const maxRole = Math.max(1, ...(d.distribution.by_role||[]).map(x=>x.count));

  return (
    <Layout>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Executive Dashboard</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Workforce Command Center</h1>
          <p className="text-sm text-slate-500 mt-1">Real-time visibility across ConnectMyHub Direct™, Managed™, AI™, Hybrid™ and Teams™.</p>
        </div>
        <div className="text-xs text-slate-500 flex items-center gap-1.5"><Clock size={13}/> Live · updated moments ago</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard testId="kpi-placement-revenue" icon={DollarSign} label="Placement Revenue YTD" value={`$${(r.placement_ytd/1000).toFixed(1)}k`} sub={`Avg fee $${r.avg_placement_fee.toLocaleString()}`} tone="emerald" />
        <StatCard testId="kpi-open-jobs" icon={Briefcase} label="Open Job Orders" value={j.open} sub={`${j.filled} filled · ${j.total} total`} tone="sky" />
        <StatCard testId="kpi-candidates-pipeline" icon={Users} label="Candidates In Pipeline" value={c.in_pipeline} sub={`${c.approved} ConnectMyHub Approved`} tone="indigo" />
        <StatCard testId="kpi-active-clients" icon={Building2} label="Active Clients" value={co.active_clients} sub={`${co.prospects} prospects`} tone="amber" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard testId="kpi-approved" icon={Award} label="ConnectMyHub Approved" value={c.approved} sub="Ready to present" tone="emerald" />
        <StatCard testId="kpi-available" icon={Sparkles} label="Available Talent" value={c.available} sub="Bench strength" tone="sky" />
        <StatCard testId="kpi-total-candidates" icon={Users} label="Total Talent" value={c.total} sub="Across all statuses" tone="slate" />
        <StatCard testId="kpi-membership-mrr" icon={TrendingUp} label="Recurring MRR" value={`$${r.recurring_mrr}`} sub="Memberships + Managed" tone="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Globe2 size={14} className="text-slate-500"/>
            <h3 className="text-sm font-semibold text-slate-900">Talent by country</h3>
          </div>
          {(d.distribution.by_country || []).length === 0 ? <div className="text-xs text-slate-500">No data yet.</div> :
            d.distribution.by_country.map(x => <DistBar key={x.label} label={x.label} count={x.count} max={maxCountry}/>)}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Briefcase size={14} className="text-slate-500"/>
            <h3 className="text-sm font-semibold text-slate-900">Roles distribution</h3>
          </div>
          {(d.distribution.by_role || []).length === 0 ? <div className="text-xs text-slate-500">No data yet.</div> :
            d.distribution.by_role.map(x => <DistBar key={x.label} label={x.label} count={x.count} max={maxRole}/>)}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Building2 size={14} className="text-slate-500"/>
            <h3 className="text-sm font-semibold text-slate-900">Client industries</h3>
          </div>
          {(d.distribution.by_industry || []).length === 0 ? <div className="text-xs text-slate-500">No data yet.</div> :
            d.distribution.by_industry.map(x => <DistBar key={x.label} label={x.label} count={x.count} max={Math.max(1,...d.distribution.by_industry.map(y=>y.count))}/>)}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-900 mb-4">Recent activity</h3>
        {(d.recent_activity || []).length === 0 ? <div className="text-xs text-slate-500">No recent activity.</div> :
          <div className="space-y-2.5">
            {d.recent_activity.slice(0,10).map(a => (
              <div key={a.id} className="flex items-center gap-3 text-xs">
                <div className="w-1.5 h-1.5 rounded-full bg-sky-500 shrink-0"></div>
                <div className="flex-1 min-w-0 truncate"><span className="font-semibold text-slate-900">{a.user_name}</span> <span className="text-slate-600">{a.action.replace('.',' ')} </span> <span className="text-slate-500 truncate">{a.details?.name || a.details?.role || a.entity_id?.slice(0,8)}</span></div>
                <div className="text-slate-400 shrink-0">{new Date(a.at).toLocaleString()}</div>
              </div>
            ))}
          </div>}
      </div>

      <div className="mt-6">
        <ComplianceAttentionWidget />
      </div>
    </Layout>
  );
}
